import React, { useState, useEffect, useRef } from 'react';
import DatePicker from '../../../../../components/DatePicker/DatePicker';
import '../LiquidarContratosModal/LiquidarContratosModal.css';
import './LiquidarParcelasModal.css';

export interface ParcelaParaLiquidar {
    id_parcela: number;
    parcela: string;
    seuNumero: string;
    saldo_registrado: number;
    vl_pago: string;
}

type ModalStatus = 'idle' | 'loading' | 'success' | 'error';

interface LiquidarParcelasModalProps {
    show: boolean;
    onClose: () => void;
    onConfirm: (parcelas: Array<{ id_parcela: number; vl_pago: number; dtMovimento: string }>) => Promise<void>;
    parcelas: ParcelaParaLiquidar[];
    loading?: boolean;
    onSuccess?: () => void;
}

const LiquidarParcelasModal: React.FC<LiquidarParcelasModalProps> = ({ 
    show, 
    onClose, 
    onConfirm, 
    parcelas,
    onSuccess
}) => {
    const [dataMovimento, setDataMovimento] = useState('');
    const [valorUnico, setValorUnico] = useState('');
    const [usarValorUnico, setUsarValorUnico] = useState(true);
    const [valoresIndividuais, setValoresIndividuais] = useState<Record<number, string>>({});
    const [status, setStatus] = useState<ModalStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const prevShowRef = useRef(false);

    useEffect(() => {
        if (show && !prevShowRef.current) {
            const hoje = new Date().toISOString().split('T')[0];
            setDataMovimento(hoje);

            const valores: Record<number, string> = {};
            parcelas.forEach(p => {
                valores[p.id_parcela] = p.saldo_registrado.toFixed(2);
            });
            setValoresIndividuais(valores);
            setValorUnico('');
            setUsarValorUnico(true);
            setStatus('idle');
            setErrorMessage('');
        }
        prevShowRef.current = show;
    }, [show]);

    const handleConfirm = async () => {
        if (!dataMovimento) return;

        const parcelasParaEnviar = parcelas.map(p => {
            const valorStr = usarValorUnico ? valorUnico : (valoresIndividuais[p.id_parcela] || '0');
            const valor = parseFloat(valorStr.replace(',', '.'));
            return {
                id_parcela: p.id_parcela,
                vl_pago: valor,
                dtMovimento: dataMovimento
            };
        });

        setStatus('loading');
        setErrorMessage('');
        try {
            await onConfirm(parcelasParaEnviar);
            setStatus('success');
        } catch (error) {
            setStatus('error');
            setErrorMessage(error instanceof Error ? error.message : 'Erro desconhecido ao liquidar parcelas.');
        }
    };

    const isValorValido = (): boolean => {
        if (!dataMovimento) return false;

        if (usarValorUnico) {
            const valor = parseFloat(valorUnico.replace(',', '.'));
            return !isNaN(valor) && valor > 0;
        } else {
            return parcelas.every(p => {
                const valorStr = valoresIndividuais[p.id_parcela] || '0';
                const valor = parseFloat(valorStr.replace(',', '.'));
                return !isNaN(valor) && valor > 0;
            });
        }
    };

    const sanitizeValor = (valor: string): string => {
        const semLetras = valor.replace(/[^0-9,.]/g, '');
        const partes = semLetras.split(/[,.]/);
        if (partes.length > 2) {
            return partes[0] + ',' + partes.slice(1).join('');
        }
        return semLetras.replace('.', ',');
    };

    const handleValorIndividualChange = (id_parcela: number, valor: string) => {
        setValoresIndividuais(prev => ({
            ...prev,
            [id_parcela]: sanitizeValor(valor)
        }));
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const isLoading = status === 'loading';

    if (!show) return null;

    return (
        <div className="liquidar-modal-overlay" onClick={isLoading ? undefined : onClose}>
            <div className="liquidar-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="liquidar-modal-header">
                    <h3>Liquidar Parcelas</h3>
                    {!isLoading && <button className="liquidar-modal-close" onClick={onClose}>×</button>}
                </div>

                {status === 'loading' && (
                    <div className="liquidar-status-container">
                        <div className="liquidar-spinner" />
                        <p className="liquidar-status-text">Processando liquidação...</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="liquidar-status-container">
                        <div className="liquidar-status-icon liquidar-status-icon--success">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M7 12.5L10.5 16L17 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <p className="liquidar-status-text liquidar-status-text--success">Parcelas liquidadas com sucesso!</p>
                        <div className="liquidar-modal-footer">
                            <button className="liquidar-modal-button liquidar-modal-button-confirm" onClick={() => { onClose(); onSuccess?.(); }}>
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="liquidar-status-container">
                        <div className="liquidar-status-icon liquidar-status-icon--error">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M8 8L16 16M16 8L8 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <p className="liquidar-status-text liquidar-status-text--error">Erro ao liquidar parcelas</p>
                        {errorMessage && <p className="liquidar-status-detail">{errorMessage}</p>}
                        <div className="liquidar-modal-footer">
                            <button className="liquidar-modal-button liquidar-modal-button-cancel" onClick={() => setStatus('idle')}>
                                Tentar novamente
                            </button>
                            <button className="liquidar-modal-button liquidar-modal-button-confirm" onClick={onClose}>
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                {status === 'idle' && (
                    <>
                        <div className="liquidar-modal-body">
                            <div className="liquidar-warning-icon">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <p className="liquidar-modal-message">
                                Você está prestes a liquidar <strong>{parcelas.length}</strong> {parcelas.length === 1 ? 'parcela' : 'parcelas'}.
                            </p>

                            <div className="liquidar-parcelas-campo">
                                <DatePicker
                                    label="Data do Movimento"
                                    value={dataMovimento}
                                    onChange={(value) => setDataMovimento(value)}
                                />
                            </div>

                            <div className="liquidar-parcelas-tipo-valor">
                                <label className="liquidar-parcelas-radio">
                                    <input
                                        type="radio"
                                        checked={usarValorUnico}
                                        onChange={() => setUsarValorUnico(true)}
                                    />
                                    <span>Valor único para todas as parcelas</span>
                                </label>
                                <label className="liquidar-parcelas-radio">
                                    <input
                                        type="radio"
                                        checked={!usarValorUnico}
                                        onChange={() => setUsarValorUnico(false)}
                                    />
                                    <span>Valores individuais para cada parcela</span>
                                </label>
                            </div>

                            {usarValorUnico ? (
                                <div className="liquidar-parcelas-campo">
                                    <label className="liquidar-parcelas-label">Valor Pago:</label>
                                    <input
                                        type="text"
                                        className="liquidar-parcelas-input"
                                        placeholder="0,00"
                                        value={valorUnico}
                                        onChange={(e) => setValorUnico(sanitizeValor(e.target.value))}
                                    />
                                    <small className="liquidar-parcelas-hint">Use vírgula para decimais (ex: 1944,57)</small>
                                </div>
                            ) : (
                                <div className="liquidar-parcelas-list">
                                    <p className="liquidar-parcelas-list-title">Valores por parcela:</p>
                                    {parcelas.map(p => (
                                        <div key={p.id_parcela} className="liquidar-parcela-item">
                                            <div className="liquidar-parcela-info">
                                                <strong>Parcela {p.parcela}{p.seuNumero ? ` - ${p.seuNumero}` : ''}</strong>
                                                <span className="liquidar-parcela-saldo">
                                                    Saldo: {formatCurrency(p.saldo_registrado)}
                                                </span>
                                            </div>
                                            <input
                                                type="text"
                                                className="liquidar-parcelas-input-small"
                                                placeholder="0,00"
                                                value={valoresIndividuais[p.id_parcela] || ''}
                                                onChange={(e) => handleValorIndividualChange(p.id_parcela, e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <p className="liquidar-modal-warning">
                                Esta ação não pode ser desfeita. Deseja continuar?
                            </p>
                        </div>
                        <div className="liquidar-modal-footer">
                            <button 
                                className="liquidar-modal-button liquidar-modal-button-cancel" 
                                onClick={onClose}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="liquidar-modal-button liquidar-modal-button-confirm" 
                                onClick={handleConfirm}
                                disabled={!isValorValido()}
                                title={!isValorValido() ? 'Informe valores maiores que zero para todas as parcelas' : ''}
                            >
                                Confirmar Liquidação
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LiquidarParcelasModal;
